import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Loader2, Bell } from 'lucide-react';
import {
    isPushSupported,
    requestNotificationPermission,
    registerAndSubscribe
} from '../services/pushService';
import { useAuth } from '../auth/AuthContext';
import './NotificationVerification.css';
import { useToast } from './toast/ToastProvider';

interface VerificationStatus {
    permission: 'checking' | 'granted' | 'denied' | 'default' | 'not-supported';
    serviceWorker: 'checking' | 'registered' | 'not-registered' | 'not-supported';
    subscription: 'checking' | 'subscribed' | 'not-subscribed' | 'error';
    subscriptionDetails?: {
        endpoint: string;
        expirationTime: number | null;
    };
}

const NotificationVerification: React.FC = () => {
    const { session } = useAuth();
    const user = session?.user;
    const toast = useToast();
    const [status, setStatus] = useState<VerificationStatus>({
        permission: 'checking',
        serviceWorker: 'checking',
        subscription: 'checking'
    });
    const [isChecking, setIsChecking] = useState(false);
    const [testNotificationSent, setTestNotificationSent] = useState(false);

    useEffect(() => {
        checkAllStatus();
    }, []);

    const checkAllStatus = async () => {
        setIsChecking(true);

        // Check notification permission
        if (!('Notification' in window)) {
            setStatus(prev => ({ ...prev, permission: 'not-supported' }));
        } else {
            const permission = Notification.permission;
            setStatus(prev => ({ ...prev, permission: permission as any }));
        }

        // Check service worker
        if (!('serviceWorker' in navigator)) {
            setStatus(prev => ({ ...prev, serviceWorker: 'not-supported' }));
        } else {
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                if (registration) {
                    setStatus(prev => ({ ...prev, serviceWorker: 'registered' }));
                } else {
                    setStatus(prev => ({ ...prev, serviceWorker: 'not-registered' }));
                }
            } catch (e) {
                setStatus(prev => ({ ...prev, serviceWorker: 'not-registered' }));
            }
        }

        // Check subscription
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                const subJson = subscription.toJSON();
                setStatus(prev => ({
                    ...prev,
                    subscription: 'subscribed',
                    subscriptionDetails: {
                        endpoint: subJson.endpoint || '',
                        expirationTime: subscription.expirationTime
                    }
                }));
            } else {
                setStatus(prev => ({ ...prev, subscription: 'not-subscribed' }));
            }
        } catch (e) {
            setStatus(prev => ({ ...prev, subscription: 'error' }));
        }

        setIsChecking(false);
    };

    const handleRequestPermission = async () => {
        try {
            const permission = await requestNotificationPermission();
            setStatus(prev => ({ ...prev, permission: permission as any }));
            if (permission === 'granted' && user) {
                // Auto-subscribe after permission granted
                await handleSubscribe();
            }
        } catch (e) {
            console.error('Error requesting permission:', e);
        }
    };

    const handleSubscribe = async () => {
        if (!user?.id) {
            toast.warning('ログインが必要です', { title: '操作できません' });
            return;
        }

        try {
            setIsChecking(true);
            await registerAndSubscribe(user.id);
            await checkAllStatus();
        } catch (e) {
            console.error('Error subscribing:', e);
            toast.error(`サブスクリプションに失敗しました: ${(e as Error).message}`, { title: '購読失敗' });
        } finally {
            setIsChecking(false);
        }
    };

    const handleTestNotification = async () => {
        try {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification('テスト通知', {
                body: 'プッシュ通知が正常に動作しています！',
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: 'test-notification',
                data: { url: '/job-list' },
                requireInteraction: true
            });
            setTestNotificationSent(true);
            setTimeout(() => setTestNotificationSent(false), 3000);
        } catch (e) {
            console.error('Error showing test notification:', e);
            toast.error(`通知の表示に失敗しました: ${(e as Error).message}`, { title: '通知失敗' });
        }
    };

    const getStatusIcon = (statusValue: string) => {
        if (statusValue === 'checking') {
            return <Loader2 size={20} className="spinning" />;
        }
        if (statusValue === 'granted' || statusValue === 'registered' || statusValue === 'subscribed') {
            return <CheckCircle2 size={20} className="success" />;
        }
        if (statusValue === 'not-supported') {
            return <AlertCircle size={20} className="warning" />;
        }
        return <XCircle size={20} className="error" />;
    };

    const getStatusText = (type: keyof VerificationStatus) => {
        const value = status[type];
        switch (type) {
            case 'permission':
                switch (value) {
                    case 'granted': return '許可済み';
                    case 'denied': return '拒否';
                    case 'default': return '未設定';
                    case 'not-supported': return 'サポートされていません';
                    default: return '確認中...';
                }
            case 'serviceWorker':
                switch (value) {
                    case 'registered': return '登録済み';
                    case 'not-registered': return '未登録';
                    case 'not-supported': return 'サポートされていません';
                    default: return '確認中...';
                }
            case 'subscription':
                switch (value) {
                    case 'subscribed': return 'サブスクライブ済み';
                    case 'not-subscribed': return '未サブスクライブ';
                    case 'error': return 'エラー';
                    default: return '確認中...';
                }
            default:
                return '';
        }
    };

    const allReady =
        status.permission === 'granted' &&
        status.serviceWorker === 'registered' &&
        status.subscription === 'subscribed';

    return (
        <div className="notification-verification">
            <div className="verification-header">
                <Bell size={24} />
                <h3>プッシュ通知セットアップ確認</h3>
            </div>

            <div className="verification-status">
                <div className="status-item">
                    <div className="status-icon">
                        {getStatusIcon(status.permission)}
                    </div>
                    <div className="status-content">
                        <div className="status-label">通知許可</div>
                        <div className="status-value">{getStatusText('permission')}</div>
                    </div>
                    {status.permission === 'default' && (
                        <button
                            className="btn-action"
                            onClick={handleRequestPermission}
                            disabled={isChecking}
                        >
                            許可をリクエスト
                        </button>
                    )}
                </div>

                <div className="status-item">
                    <div className="status-icon">
                        {getStatusIcon(status.serviceWorker)}
                    </div>
                    <div className="status-content">
                        <div className="status-label">Service Worker</div>
                        <div className="status-value">{getStatusText('serviceWorker')}</div>
                    </div>
                </div>

                <div className="status-item">
                    <div className="status-icon">
                        {getStatusIcon(status.subscription)}
                    </div>
                    <div className="status-content">
                        <div className="status-label">プッシュサブスクリプション</div>
                        <div className="status-value">{getStatusText('subscription')}</div>
                        {status.subscriptionDetails && (
                            <div className="subscription-details">
                                <div className="detail-item">
                                    <span className="detail-label">エンドポイント:</span>
                                    <span className="detail-value">
                                        {status.subscriptionDetails.endpoint.substring(0, 50)}...
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {status.permission === 'granted' && status.subscription === 'not-subscribed' && (
                        <button
                            className="btn-action"
                            onClick={handleSubscribe}
                            disabled={isChecking || !user}
                        >
                            サブスクライブ
                        </button>
                    )}
                </div>
            </div>

            {allReady && (
                <div className="verification-actions">
                    <button
                        className="btn btn-primary"
                        onClick={handleTestNotification}
                        disabled={isChecking}
                    >
                        <Bell size={16} />
                        テスト通知を送信
                    </button>
                    {testNotificationSent && (
                        <div className="test-success">
                            ✅ テスト通知を送信しました。画面を確認してください。
                        </div>
                    )}
                </div>
            )}

            <div className="verification-actions">
                <button
                    className="btn btn-secondary"
                    onClick={checkAllStatus}
                    disabled={isChecking}
                >
                    {isChecking ? '確認中...' : '状態を再確認'}
                </button>
            </div>

            {!isPushSupported() && (
                <div className="verification-warning">
                    <AlertCircle size={20} />
                    <span>このブラウザはプッシュ通知をサポートしていません。Chrome、Firefox、またはEdgeを使用してください。</span>
                </div>
            )}
        </div>
    );
};

export default NotificationVerification;

